import React, { ReactText, useState } from "react"
import ReactModal from "react-modal"
import { useMediaQuery } from "react-responsive"
import tw, { styled, css } from "twin.macro"

// Utils
import { SlideProps } from "."
import { modalStyles } from "../utils/modalStyles"

// Components
import CloseIcon from "../svgs/closeIcon"
import Container from "../components/container"
import IconBrakes from "../icons/iconBrakes"
import IconDrivelines from "../icons/iconDrivelines"
import IconPetrolDiesel from "../icons/iconPetrolDiesel"
import IconSafety from "../icons/iconSafety"
import IconSuspension from "../icons/iconSuspension"
import IconTools from "../icons/iconTools"
import IconTyres from "../icons/iconTyres"
import LearnDetail from "../components/learnDetail"
import NextPageButton from "../components/nextPageButton"
import SlideSection from "../components/slideSection"
import TitleLearn from "../svgs/titleLearn"
import NextPageContainer from "../components/nextPageContainer"

interface TopicData {
  icon: React.FC
  description: ReactText
  details?: TopicDetails[]
}

interface TopicDetails {
  title?: string
  text: string[]
}

const topics: TopicData[] = [
  {
    icon: IconSafety,
    description:
      "Good work habits and safe work practices in the motor industry",
  },
  {
    icon: IconTools,
    description: "Tools and equipment used in the motor industry",
  },
  {
    icon: IconPetrolDiesel,
    description: "Petrol and diesel engines ",
  },
  {
    icon: IconDrivelines,
    description: "Drivelines and final drives",
  },
  {
    icon: IconSuspension,
    description: "Steering and suspension systems",
  },
  {
    icon: IconTyres,
    description: "Automotive tyres and wheels",
  },
  {
    icon: IconBrakes,
    description: "Brake systems",
  },
]

const LearnSlide: React.SFC<SlideProps> = ({
  slideVisible,
  nextButtonClicked,
}) => {
  const [topicIndex, setTopicIndex] = useState<number>(-1)

  const isSmall = useMediaQuery({ query: "(max-width: 767px)" })

  return (
    <SlideSection autoHeightResponsive>
      <div css={[tw`flex flex-col justify-center items-center`]}>
        <Container>
          <Columns>
            <LargeOneQuarter>
              <TitleWrapper>
                <TitleLearn />
                <Instructions>
                  A MITO Micro&reg; Introduction to Automotive Engineering will give
                  you a solid knowledge foundation to build on in the future.
                </Instructions>
              </TitleWrapper>
            </LargeOneQuarter>
            <LargeThreeQuarters>
              <Topics>
                {" "}
                {topics.map((topic, index) => {
                  const delay = `transition-delay: ${
                    slideVisible ? 250 + index * 175 : 0
                  }ms`
                  return (
                    <React.Fragment key={index}>
                      <Topic>
                        {/* Fade + translate the icon in */}
                        <div
                          css={[
                            slideVisible &&
                              css`
                                ${delay}
                              `,
                            tw`transition duration-500 ease-out`,
                            !slideVisible &&
                              tw`opacity-0 transform -translate-y-8`,
                          ]}
                        >
                          <topic.icon />
                        </div>
                        {/* Fade the text in */}
                        <div
                          css={[
                            css`
                              ${delay}
                            `,
                            tw`transition duration-1000 ease-out`,
                            !slideVisible && tw`opacity-0`,
                          ]}
                        >
                          <TopicDescription>
                            {topic.description}
                          </TopicDescription>
                        </div>

                        {/* Show learn more if it has more  */}
                        {topic.details?.length ?? 0 > 0 ? (
                          <a href="#" onClick={() => setTopicIndex(index)}>
                            Learn more
                          </a>
                        ) : undefined}
                      </Topic>

                      {/* Force wraps for large */}
                      {/* 2 / 3 / 2 */}
                      {index == 1 && <LargeForceWrap />}
                      {index == 4 && <LargeForceWrap />}

                      {/* Force wraps for medium */}
                    </React.Fragment>
                  )
                })}
              </Topics>
            </LargeThreeQuarters>
          </Columns>
        </Container>
        <NextPageContainer>
          <NextPageButton
            onClick={nextButtonClicked}
            label="Enrol now for free"
          />
        </NextPageContainer>
      </div>

      {/* When we select 'show more' display it in a modal */}
      <ReactModal
        style={modalStyles(isSmall)}
        isOpen={topicIndex >= 0}
        onRequestClose={() => setTopicIndex(-1)}
      >
        {topicIndex >= 0 && topics[topicIndex]?.details && (
          <LearnDetail items={topics[topicIndex].details!} />
        )}
        <div css={[tw`absolute top-0 right-0`]}>
          <button
            css={[tw`p-4 focus:outline-none`]}
            onClick={() => setTopicIndex(-1)}
          >
            <CloseIcon />
          </button>
        </div>
      </ReactModal>
    </SlideSection>
  )
}

const LargeForceWrap = () => (
  <div
    css={[
      css`
        flex-basis: 100%;
        height: 0;
      `,
      tw`hidden lg:block`,
    ]}
  />
)

const Columns = styled.div`
  ${tw`flex flex-col lg:flex-row items-center justify-center w-full`}
`

const LargeThreeQuarters = styled.div`
  ${tw`lg:w-3/5 flex flex-col items-center justify-center`}
`

const LargeOneQuarter = styled.div`
  ${tw`lg:w-2/5 flex flex-col items-center justify-center`}
`

const Instructions = styled.p`
  ${tw`w-64 text-center font-sans text-sm mb-6`}
`

const TitleWrapper = styled.div`
  ${tw`flex flex-col items-center justify-center`}
`

const Topics = styled.div`
  ${tw`flex flex-col flex-wrap flex-row justify-center`}
`

const Topic = styled.div`
  ${tw`flex flex-col items-center w-1/2 md:w-1/3 mb-12 px-8`}
`

const TopicDescription = styled.p`
  max-width: 9rem;
  ${tw`font-sans text-sm text-center mt-3 whitespace-pre-line`}
`
export default LearnSlide
